import { useMemo, useState } from 'react';

import { IconDots } from '@tabler/icons-react';
import { BottomNavigationAction } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { Capability } from '@repo/utils/auth';
import MenuList from '../MenuList';
import { getFooterMenuOptions, getMenuSections } from '../menu';
import { getPinnedMenuItems, getSheetSections } from '../menuSelectors';
import ProfileMenu from '../ProfileMenu';
import { isInPaths } from '../utils';

import {
  Puller,
  SheetFooterDivider,
  SheetScroll,
  StyledBottomNavigation,
  StyledSwipeableDrawer,
} from './index.styles';

const MORE_VALUE = 'more';

interface BottomNavProps {
  /** The reader's capabilities, or `undefined` while they are read, when no entry is drawn. */
  capabilities?: readonly Capability[];
}

const BottomNav = ({ capabilities }: BottomNavProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const sections = useMemo(
    () => (capabilities ? getMenuSections(capabilities) : []),
    [capabilities]
  );
  const pinned = useMemo(() => getPinnedMenuItems(sections), [sections]);
  const sheetSections = useMemo(() => getSheetSections(sections, pinned), [sections, pinned]);
  const footerOptions = useMemo(
    () => (capabilities ? getFooterMenuOptions(capabilities) : []),
    [capabilities]
  );

  const openSheet = () => setOpen(true);
  const closeSheet = () => setOpen(false);

  // The active pinned tab, if the current route maps to one. Otherwise the page
  // lives inside the sheet, so "More" carries the selection.
  const activePinned = pinned.find(item => isInPaths(item.linkedEndpoints, pathname));

  return (
    <>
      <StyledBottomNavigation showLabels value={activePinned?.name ?? MORE_VALUE}>
        {pinned.map(item => {
          const selected = isInPaths(item.linkedEndpoints, pathname);
          return (
            <BottomNavigationAction
              key={item.name}
              value={item.name}
              label={item.name}
              icon={selected && item.iconFilled ? item.iconFilled : item.icon}
              onClick={() => navigate(item.href)}
            />
          );
        })}
        <BottomNavigationAction
          key={MORE_VALUE}
          value={MORE_VALUE}
          label='More'
          icon={<IconDots />}
          onClick={openSheet}
        />
      </StyledBottomNavigation>

      <StyledSwipeableDrawer
        anchor='bottom'
        open={open}
        onOpen={openSheet}
        onClose={closeSheet}
        disableSwipeToOpen
        ModalProps={{ keepMounted: true }}
      >
        <Puller />
        <SheetScroll>
          {sheetSections.map((section, index) => (
            <MenuList
              key={section.header ?? `section-${index}`}
              header={section.header}
              source={section.items}
              currentPath={pathname}
              expanded
              onItemClick={closeSheet}
            />
          ))}
          {footerOptions.length > 0 && (
            <>
              <SheetFooterDivider />
              <MenuList
                expanded
                currentPath={pathname}
                source={footerOptions}
                onItemClick={closeSheet}
              />
            </>
          )}
          <ProfileMenu onNavigate={closeSheet} />
        </SheetScroll>
      </StyledSwipeableDrawer>
    </>
  );
};

export default BottomNav;
